import { isDefined } from '@sgme/fp';
import type { QuoteInfo, ScheduledRunOccurrenceQuote } from 'apiConnector/scheduledRun/model';

export type ValidOccurrenceQuote = ScheduledRunOccurrenceQuote & {
  quoteId: string;
  productSubType: string;
  productType: string;
  wrapper: string;
};

export type OccurrenceReloadGroup = {
  productType: string;
  orderedQuotes: readonly ValidOccurrenceQuote[];
};

export const OCCURRENCE_RELOAD_OUTCOME = {
  EMPTY: 'empty',
  INVALID: 'invalid',
  READY: 'ready',
} as const;

export type OccurrenceReloadOutcome = (typeof OCCURRENCE_RELOAD_OUTCOME)[keyof typeof OCCURRENCE_RELOAD_OUTCOME];

export const OCCURRENCE_RELOAD_INVALID_REASON = {
  MISSING_PRODUCT_METADATA: 'missing-product-metadata',
} as const;

export type OccurrenceReloadInvalidReason =
  (typeof OCCURRENCE_RELOAD_INVALID_REASON)[keyof typeof OCCURRENCE_RELOAD_INVALID_REASON];

export type OccurrenceValidityResult = {
  outcome: OccurrenceReloadOutcome;
  reason?: OccurrenceReloadInvalidReason;
  groups?: readonly OccurrenceReloadGroup[];
  skippedCount?: number;
};

export function validateOccurrenceBeforeReload(
  scheduleQuotes: readonly QuoteInfo[],
  occurrenceQuotes: readonly ScheduledRunOccurrenceQuote[],
): OccurrenceValidityResult {
  let skippedCount = 0;
  const validQuotes: ValidOccurrenceQuote[] = [];

  for (const occurrenceQuote of occurrenceQuotes) {
    if (!isNonBlank(occurrenceQuote.quoteId)) {
      skippedCount += 1;
      continue;
    }

    if (!hasRequiredProductMetadata(occurrenceQuote)) {
      return {
        outcome: OCCURRENCE_RELOAD_OUTCOME.INVALID,
        reason: OCCURRENCE_RELOAD_INVALID_REASON.MISSING_PRODUCT_METADATA,
      };
    }

    validQuotes.push(occurrenceQuote);
  }

  if (validQuotes.length === 0) {
    return { outcome: OCCURRENCE_RELOAD_OUTCOME.EMPTY };
  }

  const remainingQuotes = [...validQuotes];
  const orderedQuotes: ValidOccurrenceQuote[] = [];

  for (const scheduleQuote of scheduleQuotes) {
    const { quoteId } = scheduleQuote;
    const matchingIndex = isDefined(quoteId)
      ? remainingQuotes.findIndex(occurrenceQuote => occurrenceQuote.quoteId === quoteId)
      : -1;
    if (matchingIndex >= 0) {
      orderedQuotes.push(remainingQuotes[matchingIndex]);
      remainingQuotes.splice(matchingIndex, 1);
    }
  }

  orderedQuotes.push(...remainingQuotes);

  const groupsByProductType = new Map<string, OccurrenceReloadGroup>();
  for (const quote of orderedQuotes) {
    const group = groupsByProductType.get(quote.productType);
    if (group) {
      groupsByProductType.set(quote.productType, { ...group, orderedQuotes: [...group.orderedQuotes, quote] });
    } else {
      groupsByProductType.set(quote.productType, {
        productType: quote.productType,
        orderedQuotes: [quote],
      });
    }
  }

  return {
    outcome: OCCURRENCE_RELOAD_OUTCOME.READY,
    groups: [...groupsByProductType.values()],
    skippedCount,
  };
}

function hasRequiredProductMetadata(quote: ScheduledRunOccurrenceQuote): quote is ValidOccurrenceQuote {
  return [quote.quoteId, quote.productType, quote.productSubType, quote.wrapper].every(isNonBlank);
}

function isNonBlank(value: string | undefined): value is string {
  return isDefined(value) && value.trim().length > 0;
}
